"use client";
import { DatePicker } from "@mui/x-date-pickers";
import {
  AdapterDayjs,
  IconButton,
  InputAdornment,
  LocalizationProvider,
  VeBox,
  VeTypography,
} from "../../mui";
import dayjs from "dayjs";
import { useController } from "react-hook-form";
import { useState } from "react";

const CalendarTodayIcon = () => {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M8.25 3V7.5M15.75 3V7.5M3.75 9.75H20.25M7.5 12.75V14.25M12 12.75V14.25M16.5 12.75V14.25M16.5 15.75V17.25M12 15.75V17.25M7.5 15.75V17.25M3.75 5.25H20.25V20.25H3.75V5.25Z"
        stroke="#5F6368"
        stroke-width="1.5"
        stroke-linejoin="round"
      />
    </svg>
  );
};

export function VeDatePicker({
  setValue,
  label,
  value,
  name,
  onChange,
  minDate,
  maxDate,
  defaultValue,
  disabled,
  setDate,
  control,
  error,
  errorText,
  required = false,
  requiredLabel,
  disableFuture = false,
  disablePast = false,
  views,
  format = "DD-MM-YYYY", // If you need only date , or any other field instead of all
  shouldDisableDate,
  openTo,
}: {
  setValue?: any;
  label: string;
  name?: string;
  value?: any;
  onChange?: any;
  minDate?: any;
  maxDate?: any;
  defaultValue?: any;
  disabled?: any;
  timezone?: any;
  setDate?: any;
  control?: any;
  error?: boolean;
  errorText?: string;
  required?: boolean;
  requiredLabel?: boolean;
  disableFuture?: boolean;
  disablePast?: boolean;
  views?: any;
  format?: string;
  openTo?: any;
  shouldDisableDate?: (date: any) => boolean;
}) {
  const {
    field: { value: controlValue, onChange: controlOnChange },
  } = control
    ? useController({
        name: name || "",
        control,
      })
    : { field: { value: "", onChange: () => {} } };

  const [internalError, setInternalError] = useState(false);

  const [open, setOpen] = useState(false);

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          value={
            control
              ? controlValue?.length > 1
                ? dayjs(controlValue)
                : null
              : value
                ? dayjs(value)
                : null
          }
          disabled={disabled}
          defaultValue={defaultValue}
          // timezone={timezone}
          openTo={openTo}
          sx={{
            width: "100%",
            maxHeight: "40px",
            "& .MuiOutlinedInput-input": { height: "12px" },
            "& .MuiSvgIcon-root": {
              fontSize: "20px", // Or your desired size (e.g., "15px")
            },
            "& .MuiOutlinedInput-root": {
              borderRadius: "4px !important",
            },
          }}
          maxDate={maxDate}
          disableFuture={disableFuture}
          disablePast={disablePast}
          minDate={minDate}
          views={views}
          format={format}
          shouldDisableDate={shouldDisableDate}
          onChange={(newValue) => {
            if (!newValue || !dayjs(newValue).isValid()) {
              // Handle clearing the value
              if (control) {
                controlOnChange(null);
              }
              if (setDate) {
                setDate(null);
              }
              if (setValue) {
                setValue(name, null, { shouldValidate: true });
              }
              if (onChange) {
                onChange(null);
              }
              return;
            }
            // Handle valid date selection
            const selectedDate = dayjs(newValue)
              .hour(12)
              .minute(0)
              .second(0)
              .millisecond(0)
              .toISOString();

            // NOTE: This was subtracting 5hrs:30 mins from the selected date. so commented
            // const selectedDate = dayjs(newValue).toDate().toISOString();

            if (control) {
              controlOnChange(selectedDate);
            }
            if (onChange) {
              onChange(selectedDate);
            }
            if (setDate) {
              setDate(selectedDate);
            }
            if (setValue) {
              setValue(name, selectedDate, { shouldValidate: true }); // Assuming setValue expects a string
            }
          }}
          label={
            required || requiredLabel ? (
              <VeBox
                sx={{
                  color: disabled ? "#8F9295 !important" : "#3D465A !important",
                }}
                display={"flex"}
              >
                {label}
                <VeBox marginLeft={"2px"} color={"red"}>
                  *
                </VeBox>
              </VeBox>
            ) : (
              <VeBox
                sx={{
                  color: disabled ? "#8F9295 !important" : "#3D465A !important",
                }}
              >
                {label}
              </VeBox>
            )
          }
          onError={(reason) => {
            setInternalError(!!reason);
          }}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          slotProps={{
            textField: {
              error: internalError || !!error,
              InputProps: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={(e: any) => {
                        // prevent input blur
                        e.stopPropagation();
                        setOpen(true);
                      }}
                    >
                      <CalendarTodayIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            },
          }}
        />
      </LocalizationProvider>
      {error && errorText && (
        <VeBox width={"100%"}>
          <VeTypography
            style={{ marginLeft: "10px" }}
            variant="label4ErrorPrimary"
          >
            {errorText}
          </VeTypography>
        </VeBox>
      )}
      {/* {(internalError) && (
        <VeBox>
          <VeTypography
            style={{ marginLeft: "10px" }}
            variant="label4ErrorPrimary"
          >
            {"Invalid date. Please select a valid date."}
          </VeTypography>
        </VeBox>
      )} */}
    </>
  );
}
